import * as React from 'react'

import * as ReactDOM from 'react-dom'

import { IAttachment } from '~/client/graph'
import {
  FileKind,
  getFileKind,
  getFileTypeText,
} from '~/client/src/shared/components/FilesInput/fileKind'
import FileTypeIcon from '~/client/src/shared/components/FilesInput/FileTypeIcon'

interface FilePreviewPopupProps {
  attachment: IAttachment | null
  isVisible: boolean
  position: { x: number; y: number }
}

const PREVIEW_WIDTH = 320
const PREVIEW_HEIGHT = 280
const OFFSET_X = 10
const OFFSET_Y = -140

function PreviewContent({
  attachment,
  kind,
}: {
  attachment: IAttachment
  kind: FileKind
}) {
  const [imageError, setImageError] = React.useState(false)

  if (kind === 'image' && !imageError) {
    return (
      <div className="tw:flex tw:items-center tw:justify-center tw:h-full">
        <img
          src={attachment.url}
          alt={attachment.fileName}
          className="tw:max-w-full tw:max-h-full tw:object-contain"
          onError={() => setImageError(true)}
        />
      </div>
    )
  }

  if (kind === 'video') {
    return (
      <div className="tw:flex tw:items-center tw:justify-center tw:h-full">
        <video
          src={attachment.url}
          className="tw:max-w-full tw:max-h-full tw:object-contain"
          muted
          preload="metadata"
        />
      </div>
    )
  }

  if (kind === 'pdf') {
    return (
      <div className="tw:flex tw:items-center tw:justify-center tw:h-full">
        <iframe
          src={`${attachment.url}#toolbar=0&navpanes=0&scrollbar=0`}
          className="tw:w-full tw:h-full tw:border-none"
          title="PDF Preview"
        />
      </div>
    )
  }

  const typeText = getFileTypeText(kind) || 'File'

  return (
    <div className="tw:flex tw:flex-col tw:items-center tw:justify-center tw:h-full tw:gap-3 tw:p-4 tw:text-center">
      <FileTypeIcon kind={kind} />
      <div className="tw:flex tw:flex-col tw:gap-1 tw:min-w-0 tw:max-w-full">
        <div className="tw:text-body-md tw:font-medium tw:text-md3-on-surface tw:truncate">
          {attachment.fileName}
        </div>
        <div className="tw:text-body-sm tw:text-md3-on-surface-variant">
          {[attachment.size, typeText].filter(Boolean).join(' \u00b7 ')}
        </div>
      </div>
    </div>
  )
}

export default function FilePreviewPopup({
  attachment,
  isVisible,
  position,
}: FilePreviewPopupProps) {
  if (!isVisible || !attachment) return null

  const kind = getFileKind(attachment.fileName)

  const left = Math.min(
    position.x + OFFSET_X,
    window.innerWidth - PREVIEW_WIDTH - 8,
  )
  const top = Math.min(
    Math.max(8, position.y + OFFSET_Y),
    window.innerHeight - PREVIEW_HEIGHT - 8,
  )

  return ReactDOM.createPortal(
    <div
      style={{
        position: 'fixed',
        left,
        top,
        width: PREVIEW_WIDTH,
        height: PREVIEW_HEIGHT,
        zIndex: 10000,
        pointerEvents: 'none',
      }}
      className="tw:rounded-lg tw:border tw:border-solid tw:border-md3-outline-variant tw:bg-md3-surface tw:shadow-lg tw:overflow-hidden"
    >
      <PreviewContent attachment={attachment} kind={kind} />
    </div>,
    document.body,
  )
}
